import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { factForDay, localDayNumber, msUntilLocalMidnight } from '../fact-of-day.js';

// "Fact of the day" on Home: one reefer fact a day from the same
// admin-editable pool that footers the emails (GET /api/tips), cached in
// localStorage so it paints at once and survives an offline load. The
// next one arrives at local midnight; there is no "another" on purpose.

const CACHE_KEY = 'aqs.tips';
const PIN_KEY = 'aqs.factOfDay';

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode — best effort */
  }
}

export function TipCard() {
  const [tips, setTips] = useState(() => {
    const cached = readJson(CACHE_KEY);
    return Array.isArray(cached) && cached.length ? cached : null;
  });
  const [day, setDay] = useState(localDayNumber);

  useEffect(() => {
    let alive = true;
    api('/api/tips')
      .then((r) => {
        if (!alive || !Array.isArray(r?.tips) || !r.tips.length) return;
        setTips(r.tips);
        writeJson(CACHE_KEY, r.tips);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // A Home left open overnight turns over at midnight too. Phones suspend
  // timers in a background tab, so coming back to the page checks the
  // date again.
  useEffect(() => {
    const t = setTimeout(() => setDay(localDayNumber()), msUntilLocalMidnight() + 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') setDay(localDayNumber());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [day]);

  const fact = factForDay(tips, day, readJson(PIN_KEY));

  useEffect(() => {
    if (fact) writeJson(PIN_KEY, { day, ...fact });
  }, [day, fact?.heading]);

  if (!fact) return null;

  return (
    <div
      style={{
        borderRadius: 16,
        border: '1px solid var(--surface-border)',
        background:
          'linear-gradient(140% 160% at 12% 0%, rgba(56,189,248,0.10), rgba(127,127,127,0.03) 60%)',
        padding: '16px 18px',
      }}
    >
      <span
        className="font-heading"
        style={{
          fontSize: 13,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'var(--cyan)',
        }}
      >
        Fact of the day
      </span>
      <div style={{ fontSize: 16.5, fontWeight: 650, letterSpacing: '-0.01em', marginTop: 8 }}>
        {fact.heading}
      </div>
      <div style={{ fontSize: 15, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.55 }}>
        {fact.body}
      </div>
    </div>
  );
}
